import { Dialog, DialogContent, DialogTitle, Stack, Tab, Tabs, useTheme } from "@mui/material";
import * as React from "react";
import Alert from "../../../components/alerting/alert";

import useBackend from "../../../hooks/use_backend";
import { isDisabled, isReadOnly } from "../../../model/device";

import { RegisterLiveData } from "../../device/register_live_data";
import { EditDevice } from "../../device/edit_device";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { saveDeviceRequest } from "../../../scripts/api_v2/commands_client";
import Snackbar from "../../../scripts/snackbar";
import { REACT_QUERY_KEYS } from "../../../enums/react_query_key_enum";
import { fetchSiteData } from "../../../scripts/api_v2/site_client";
import { BatteryLiveData } from "../../device/battery_live_data";
import { EditNode } from "./edit_node";
import { DeviceMetering } from "../../device/device_metering";
import { getTypeAlias } from "../../../model/device";
import { NodeMetering } from "components/device/node_metering";

export function NodeDialog({
  open,
  schematic,
  node,
  device,
  closeModal,
  setDevice,
  permissionMap,
  required_privilege,
}) {
  const theme = useTheme();
  const [tab, setTab] = React.useState(0);
  const params = useParams();
  const [alerts, setAlerts] = React.useState([]);
  const backend = useBackend();

  const noDevice = device === null || device === undefined;

  const { mutate: saveDeviceMutation } = useMutation({
    mutationFn: async ({ edits, updatedDevice }) => {
      return await saveDeviceRequest(backend.fetch, params.site_id, edits);
    },
    onSuccess: (data, variables) => {
      const { edits, updatedDevice } = variables;
      setDevice(updatedDevice);
    },
    onError: (err, variables, context) => {
      Snackbar.error("Could not save device: " + err);
    },
  });

  let {
    data: siteData,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id],
    queryFn: () => fetchSiteData(backend.fetch, params.site_id),
    enabled: false, // sitePage will refresh the query
  });

  React.useEffect(() => {
    const alerts = siteData && device?.device_id ? siteData.alerts.filter((a) => a.device_id == device.device_id) : [];
    setAlerts(alerts);
  }, [siteData, device]);

  async function handleSaveDevice(edits, updatedDevice) {
    saveDeviceMutation({ edits, updatedDevice });
  }

  const NodeTitle = () => {
    if (noDevice) {
      return (
        <DialogTitle id="add-dialog-title" sx={{ pb: 0 }}>
          <b style={{ color: theme.palette.secondary.main }}>Empty node</b>
        </DialogTitle>
      );
    } else {
      return (
        <DialogTitle id="add-dialog-title" sx={{ pb: 0 }}>
          <b style={{ color: theme.palette.secondary.main }}>{getTypeAlias(device?.device_type)}</b>
          <b style={{ color: "lightgrey" }}> • </b>
          <b style={{ color: theme.palette.primary.main }}>{device?.name}</b>
          {device !== null && isDisabled(device)
            ? [<b style={{ color: "lightgrey" }}> • </b>, <b style={{ color: theme.palette.error.main }}> Disabled </b>]
            : isReadOnly(device)
              ? [
                  <b style={{ color: "lightgrey" }}> • </b>,
                  <b style={{ color: theme.palette.error.main }}> Read Only </b>,
                ]
              : null}
        </DialogTitle>
      );
    }
  };
  return (
    <Dialog
      open={open}
      onClose={() => {
        setTab(0);
        closeModal();
      }}
      aria-labelledby="devicedata-dialog-title"
      aria-describedby="devicedata-dialog-description"
      fullWidth
      maxWidth={"md"}
    >
      <Stack direction="column" justifyContent="space-between" alignItems="center" display="flex">
        {NodeTitle()}

        <Tabs value={tab} onChange={(event, newValue) => setTab(newValue)} aria-label="basic tabs example">
          <Tab label="Node" value={0} />
          <Tab disabled={noDevice} label="Device" value={1} />
          <Tab disabled={noDevice} label={"Alerts (" + alerts.length + ")"} value={2} />
          <Tab disabled={noDevice} label={"Metering"} value={5} />
          <Tab disabled={noDevice} label="Readings" value={3} />
          <Tab disabled={noDevice} label="Registers" value={4} />
        </Tabs>
      </Stack>
      <DialogContent
        sx={{
          minHeight: "500px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflowY: "auto",
          pt: 1,
        }}
      >
        {tab === 0 ? <EditNode schematic={schematic} node={node} handleClose={closeModal} /> : null}
        {device !== null && tab === 1 ? (
          <EditDevice
            device={device}
            setDevice={setDevice}
            handleClose={closeModal}
            handleSaveDevice={handleSaveDevice}
            permissionMap={permissionMap}
            required_privilege={required_privilege}
          />
        ) : null}
        {device !== null && tab === 3 ? <DeviceReadings device={device} /> : null}
        {device !== null && tab === 4 ? <DeviceRegisters device={device} /> : null}
        {device !== null && tab === 2 ? <AlertTab alerts={alerts} /> : null}
        {tab === 5 && (device && !noDevice ? <DeviceMetering device={device} /> : null)}
      </DialogContent>
      {/*<DialogActions sx={{justifyContent: "center", mb: 1}}>
            <Button onClick={() => {closeModal()}}>Close</Button>
        </DialogActions>*/}
    </Dialog>
  );
}

function DeviceReadings({ device }) {
  //const {liveOfDevice, meteringOfDevice, tsOfDevice} = useLiveData()
  return (
    <>
      <BatteryLiveData deviceId={device.device_id} />
      {/*liveOfDevice(device.device_id) ?
            <BatteryLiveData data={meteringOfDevice(device.device_id)} timestamp={tsOfDevice(device.device_id)}/> : null
        */}
    </>
  );
}

function DeviceRegisters({ device }) {
  //const {liveOfDevice, registersOfDevice, tsOfDevice} = useLiveData()
  return (
    <>
      <RegisterLiveData deviceId={device.device_id} />
      {/*liveOfDevice(device.device_id) ?
            <RegisterLiveData data={registersOfDevice(device.device_id)}
                              timestamp={tsOfDevice(device.device_id)}/> : null
        */}
    </>
  );
}

function AlertTab({ alerts }) {
  return (
    <div style={{ padding: 10 }}>
      {alerts.map((alert) => (
        <Alert alert={alert} />
      ))}
    </div>
  );
}
